import React, { Component, createRef, useEffect, useState } from "react";
import PropTypes from "prop-types";

import SelectField from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import DialogContent from "@mui/material/DialogContent";

import Button from "@mui/material/Button";

import generateUUID from "uuid/v4";

import { styled } from "@mui/material/styles";

import * as R from "ramda";

import { ICONS } from "~/constants/materials";

import {
  attachmentListToMap,
  revmerge,
  isNewState,
  isPendingState,
  isUploadedState,
  filesToAttachments,
  getAttachments,
  removeAttachment,
  removeDocument,
  rejectByName,
  findByName,
  uploadAttachment,
  serialAttachmentUpload
} from "~/libs/upload";

import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import DialogActions from "@mui/material/DialogActions";

import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";

import DialogHeader from "~/components/Modal/ShowMaterialModal/DialogHeader";
import InputContextMenu from "~/components/InputContextMenu";
import AttachmentsTable from "../../AttachmentsTable";

const HiddenInput = styled("input")`
  display: none;
`;

const translationMap = {
  audio: "Audio-Datei",
  file: "Datei",
  game: "Internet-Spiel",
  image: "Bild",
  link: "Link",
  video: "Video",
  worksheet: "Arbeitsblatt"
};

class NewMaterialModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      attachments: {},
      material: {
        title: "Neues Material",
        _id: generateUUID(),
        materialType: "link",
        renderType: "link",
        type: "material"
      },
      materialTypeSelectOpen: false,
      renderTypeSelectOpen: false
    };
  }

  uploadRef = createRef();

  handleOnClick = name => (event, key, payload) =>
    this.setState(({ material }) => ({
      material: R.assoc(name, payload, material)
    }));

  handleOnChange = name => event => {
    const { value } = event.target;
    this.setState(({ material }) => ({
      material: R.assoc(name, value, material)
    }));
  };

  createMaterial = () => {
    this.props.addMaterial(this.state.material);
    this.props.hideModal();
  };

  openDialog = () => this.uploadRef.current.click();

  handleFiles = event => {
    const attachments = R.reduce(
      (acc, attachment) => R.assoc(attachment.name, attachment, acc),
      {},
      filesToAttachments(event.target.files)
    );

    this.setState(
      R.evolve({
        attachments: revmerge(attachments)
      })
    );
  };

  handleUpload = () => {
    this.setState(
      R.evolve({
        attachments: R.map(R.when(isNewState, R.assoc("status", "Laden...")))
      }),
      () => {
        const pendingAttachments = R.filter(isPendingState, this.state.attachments);

        const uploadTasks = R.map(
          attachment => rev => uploadAttachment(this.state.material._id, rev, attachment),
          Object.values(pendingAttachments)
        );

        serialAttachmentUpload(uploadTasks)
          .then(attachmentListToMap)
          .then(this.handleUploadResult)
          .catch(console.error);
      }
    );
  };

  handleUploadResult = result => {
    this.setState(
      R.evolve({
        attachments: R.compose(
          R.map(attachment => {
            const upload = R.propOr({}, attachment.name, result);

            if (R.has("error", upload)) {
              return {
                ...attachment,
                status: "Fehler"
              };
            }
            if (R.has("result", upload)) {
              return {
                ...attachment,
                status: "Hochgeladen"
              };
            }

            return attachment;
          }),
          Object.values
        )
      })
    );
  };

  handleAttachmentDelete = name => _ => {
    const attachment = findByName(name, this.state.attachments);
    let task = Promise.resolve();

    if (isUploadedState(attachment)) {
      task = removeAttachment(this.state.material._id, name);
    } else if (isNewState(attachment)) {
      task = Promise.resolve();
    }

    task
      .then(() =>
        this.setState(
          R.evolve({
            attachments: rejectByName(name)
          })
        )
      )
      .then(console.log)
      .catch(console.error);
  };

  fetchAttachments = () => getAttachments(this.state.material._id).then(attachments => this.setState({ attachments }));

  onAbort = callback => _ => removeDocument(this.state.material._id).then(callback).catch(callback);

  render() {
    const { hideModal, kind } = this.props;

    const { material } = this.state;

    const hasUnuploadedAttachments = R.reject(isPendingState, this.state.attachments).length > 0;

    // If renderType is provided and not empty, then use it...else use the given materialType
    // We're going to use this to toggle the second select field
    // const type = R.ifElse(
    //   R.compose(R.isEmpty, R.propOr('', 'renderType')),
    //   R.prop('materialType'),
    //   R.prop('renderType')
    // )(material);

    const MaterialTypeIcon = R.prop(R.defaultTo("link", material.materialType), ICONS);
    const RenderTypeIcon = R.prop(R.defaultTo("link", material.renderType), ICONS);

    return (
      <>
        <InputContextMenu />
        <DialogHeader
          title={`Hinzufügen: ${this.state.material.title}`}
          isCloseableOnly={false}
          id={`${kind}-modal-title`}
        />
        <DialogContent>
          <TextField
            variant="standard"
            name="title"
            fullWidth
            label="Titel"
            placeholder="Gib einen Titel ein..."
            value={this.state.material.title}
            onChange={this.handleOnChange("title")}
          />

          <TextField
            variant="standard"
            name="linktext"
            fullWidth
            label="Linktext"
            placeholder="Gib einen Linktext ein..."
            value={R.defaultTo("", this.state.material.linktext)}
            onChange={this.handleOnChange("linktext")}
          />

          <TextField
            variant="standard"
            name="src"
            fullWidth
            label="Zielpfad"
            placeholder="Gib einen Zielpfad ein..."
            value={R.defaultTo("", this.state.material.src)}
            onChange={this.handleOnChange("src")}
          />

          <TextField
            variant="standard"
            name="description"
            multiLine
            rows={1}
            maxRows={4}
            fullWidth
            label="Beschreibung"
            placeholder="Gib eine Beschreibung ein..."
            value={R.defaultTo("", this.state.material.description)}
            onChange={this.handleOnChange("description")}
          />

          <div style={{ display: "flex" }}>
            <div style={{ flex: 1 }}>
              <FormControl variant="standard" style={{ width: "100%" }}>
                <InputLabel id="icon-label-new">Dargestelltes Icon</InputLabel>
                <SelectField
                  variant="standard"
                  labelId="icon-label-new"
                  onChange={(event, child) => {
                    this.handleOnClick("materialType")(null, null, event.target.value);
                  }}
                  onOpen={() => this.setState({ materialTypeSelectOpen: true })}
                  onClose={() => this.setState({ materialTypeSelectOpen: false })}
                  open={this.state.materialTypeSelectOpen}
                  value={R.defaultTo("link", this.state.material.materialType)}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <MaterialTypeIcon />
                      </InputAdornment>
                    )
                  }}
                  MenuProps={{
                    style: {
                      zIndex: 9999
                    }
                  }}
                >
                  {R.toPairs(ICONS).map(([name, Icon]) => (
                    <MenuItem
                      key={name}
                      value={name}
                      selected={name === R.defaultTo("link", this.state.material.materialType)}
                    >
                      <ListItemText primary={translationMap[name]} />
                      <ListItemSecondaryAction>
                        <Icon />
                      </ListItemSecondaryAction>
                    </MenuItem>
                  ))}
                </SelectField>
              </FormControl>
            </div>

            <div style={{ flex: 1 }}>
              <FormControl variant="standard" style={{ width: "100%" }}>
                <InputLabel id="material-label-new">Material Typ</InputLabel>
                <SelectField
                  variant="standard"
                  labelId="material-label-new"
                  onChange={(event, child) => {
                    this.handleOnClick("renderType")(null, null, event.target.value);
                  }}
                  onOpen={() => this.setState({ renderTypeSelectOpen: true })}
                  onClose={() => this.setState({ renderTypeSelectOpen: false })}
                  open={this.state.renderTypeSelectOpen}
                  value={R.defaultTo("link", this.state.material.renderType)}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <RenderTypeIcon />
                      </InputAdornment>
                    )
                  }}
                  MenuProps={{
                    style: {
                      zIndex: 9999
                    }
                  }}
                >
                  {R.toPairs(ICONS).map(([name, Icon]) => (
                    <MenuItem
                      key={name}
                      value={name}
                      selected={name === R.defaultTo("link", this.state.material.renderType)}
                    >
                      <ListItemText primary={translationMap[name]} />
                      <ListItemSecondaryAction>
                        <Icon />
                      </ListItemSecondaryAction>
                    </MenuItem>
                  ))}
                </SelectField>
              </FormControl>
            </div>
          </div>

          <div>
            <AttachmentsTable
              material={this.state.material}
              attachments={this.state.attachments}
              onDelete={this.handleAttachmentDelete}
              editable
            />
            <Button color="primary" variant="text" onClick={this.openDialog}>
              Material hinzufügen
            </Button>
            <Button
              disabled={hasUnuploadedAttachments}
              color="primary"
              variant="text"
              onClick={() => this.handleUpload()}
            >
              Hochladen
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="text" onClick={() => this.createMaterial()}>
            Speichern
          </Button>
          <Button color="primary" variant="text" onClick={this.onAbort(hideModal)}>
            Abbrechen
          </Button>
        </DialogActions>
        <HiddenInput id="upload" type="file" ref={this.uploadRef} onChange={this.handleFiles} multiple />
      </>
    );
  }
}

NewMaterialModal.propTypes = {
  hideModal: PropTypes.func.isRequired,
  addMaterial: PropTypes.func.isRequired,
  kind: PropTypes.string.isRequired
};

export default NewMaterialModal;
